import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import _ from 'lodash';
import { toast } from "sonner";
import { axios } from "./axios";
import { showSuccess, showError } from "../actions/globalActions";
import getApiErrorMessage from '../utils/getApiErrorMessage';

// Import shared types
import type { Division } from '@staty/shared/schemas/division';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token: string) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

/** What still points at a division — see GET /divisions/:id/usage. */
export interface DivisionUsage {
  divisionTeams: number;
  registrationTeams: number;
}

// Type for the division store state and actions
interface DivisionState {
  divisions: Division[];
  leagues: any[]; // TODO: Add League type when created
  setDivisions: (divisions: Division[]) => void;
  setLeagues: (leagues: any[]) => void;
  getDivisionById: (id: string) => Promise<void>;
  getActiveDivisionByLeagueId: (id: string) => Promise<void>;
  getActiveDivisionByLeagueName: (name: string) => Promise<void>;
  updateDivision: (division: Division, token: string, cb?: () => void) => Promise<void>;
  getActiveDivisionsByTournamentId: (id: string) => Promise<void>;
  getPreviousDivisions: (id: string, leagueName: string, contestType: string) => Promise<void>;
  getDivisionsByLeagueId: (id: string) => Promise<void>;
  getDivisionsByRegistrationId: (id: string) => Promise<void>;
  getDivisionsByLeagueName: (name: string) => Promise<void>;
  addDivision: (getToken: () => Promise<string>) => (division: Partial<Division>) => Promise<void>;
  cloneDivision: (getToken: () => Promise<string>) => (id: string, name: string, cb?: (result: any) => void) => Promise<{ error?: string; result?: any }>;
  getDivisionUsage: (divisionId: string, token: string) => Promise<DivisionUsage | null>;
  deleteDivision: (divisionId: string, token: string) => Promise<{ error?: string } | void>;
}

const useDivisionStore = create<DivisionState>()(
  immer((set) => ({
    divisions: [],
    leagues: [],

    // Methods to manipulate the state
    setDivisions: (divisions: Division[]) => {
      set((state) => {
        state.divisions = divisions;
      });
    },

    setLeagues: (leagues: any[]) => {
      set((state) => {
        state.leagues = leagues;
      });
    },

    // Asynchronous actions
    getDivisionById: async (id: string) => {
      try {
        // Populate league AND league.tournament so the full config hierarchy
        // (division > league > tournament) is available to config resolvers like
        // the stat grid's getConfig() — otherwise tournament-level config.gameStats
        // can't resolve and falls back to defaults.
        const { data } = await axios.get(`${baseUrl}/divisions/${id}?include=league.tournament,layout`);
        set((state) => {
          const index = _.findIndex(state.divisions, { _id: id });
          if (index >= 0) {
            state.divisions[index] = data.result;
          } else {
            state.divisions.push(data.result);
          }
        });
      } catch (error) {
        console.error("Error in getDivisionById:", error);
      }
    },

    getActiveDivisionByLeagueId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/active/${id}`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getActiveDivisionByLeagueId:", error);
      }
    },

    getActiveDivisionByLeagueName: async (name: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/activeName/${name}`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getActiveDivisionByLeagueName:", error);
      }
    },

    updateDivision: async (division: Division, token: string, cb?: () => void) => {
      try {
        const { result, status, error } = (await axios.put(`${baseUrl}/divisions/${division._id}?include=league,layout`, division, createAuthHeader(token))).data;
        console.log("updateDivision data: ", result, status, error);
        error ? toast.error(error) : toast.success(`Successfully Updated!`);
        set((state) => {
          const index = state.divisions.findIndex((d) => d._id === division._id);
          if (index !== -1) {
            state.divisions[index] = result;
          }
        });
        cb && cb();
      } catch (error) {
        if (!(error as any).permissionToastShown) {
          showError("Error", getApiErrorMessage(error, "Update Error"));
        }
        console.error("Error in updateDivision:", error);
      }
    },

    getActiveDivisionsByTournamentId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/active/tournament/${id}?include=league,layout`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getDivisionsByTournamentId:", error);
      }
    },

    getPreviousDivisions: async (id: string, leagueName: string, contestType: string) => {
      try {
        const seasonsResponse = await axios.get(`${baseUrl}/leagues/organizationName/${leagueName}`);
        const orderedLeagues = _.sortBy(_.filter(seasonsResponse.data.result, { contestType }), 'startDate');
        const previousIndex = _.findIndex(orderedLeagues, { _id: id }) - 1;
        const prevLeague = orderedLeagues[previousIndex < 0 ? 0 : previousIndex];

        const divisionsResponse = await axios.get(`${baseUrl}/divisions/league/${prevLeague._id}?include=league,layout`);
        set((state) => {
          state.divisions = divisionsResponse.data.result;
        });
      } catch (error) {
        console.error("Error in getPreviousDivisions:", error);
      }
    },

    getDivisionsByLeagueId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/league/${id}?include=league,layout`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getDivisionsByLeagueId:", error);
      }
    },

    getDivisionsByRegistrationId: async (id: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/registrations/${id}/divisions`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getDivisionsByRegistrationId:", error);
      }
    },

    getDivisionsByLeagueName: async (name: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/organizationName/${name}?include=league,layout`);
        set((state) => {
          state.divisions = _.unionBy(data.result, state.divisions, '_id');
        });
      } catch (error) {
        console.error("Error in getDivisionsByLeagueName:", error);
      }
    },

    addDivision: (getToken: () => Promise<string>) => {
      return async (division: Partial<Division>) => {
        try {
          const token = await getToken();
          const { error, result, status } = (await axios.post(`${baseUrl}/divisions`, division, createAuthHeader(token))).data;
          error ? toast.error(error) : toast.success(`Successfully Updated`);
          console.log('addDivision::result::', result);
          set((state) => {
            state.divisions.push(result);
          });
        } catch (error) {
          showError("Error", "Add Error");
          console.error("Error in addDivision:", error);
        }
      };
    },

    // Duplicate a division as a sibling in the same contest. The clone keeps the
    // source's config but gets NO payment product of its own — it inherits price
    // from the parent chain (see division.controller.clone).
    cloneDivision: (getToken: () => Promise<string>) => {
      return async (id: string, name: string, cb?: (result: any) => void) => {
        try {
          const token = await getToken();
          const { error, result } = (await axios.post(
            `${baseUrl}/divisions/d/${id}/clone?include=league,config.paymentProduct`,
            { name },
            createAuthHeader(token)
          )).data;
          error ? toast.error(error) : toast.success('Successfully Cloned!');
          if (!error && result) {
            set((state) => {
              state.divisions.push(result);
            });
          }
          cb && cb(result);
          return { error, result };
        } catch (error: any) {
          const message = error?.response?.data?.error || 'Clone Error';
          showError('Error', message);
          console.error('Error in cloneDivision:', error);
          return { error: message };
        }
      };
    },

    /**
     * How much still references this division. Deleting a division is a soft
     * delete with no cascade, so these rows survive pointing at the tombstone —
     * the Structure delete dialog shows the counts as a warning.
     * Returns null on failure so the caller can degrade to the plain dialog.
     */
    getDivisionUsage: async (divisionId: string, token: string) => {
      try {
        const { data } = await axios.get(`${baseUrl}/divisions/${divisionId}/usage`, createAuthHeader(token));
        return data.result || null;
      } catch (error) {
        console.error("Error in getDivisionUsage:", error);
        return null;
      }
    },

    deleteDivision: async (divisionId: string, token: string) => {
      try {
        const { error, result, status } = (await axios.delete(`${baseUrl}/divisions/${divisionId}`, createAuthHeader(token))).data;
        if (error) toast.error(error);
        console.log('deleteDivision data: ', result);
        if (result) {
          toast.success(`Successfully Removed!`);
          set((state) => {
            state.divisions = state.divisions.filter((d) => d._id !== divisionId);
          });
        } else {
          showError("Error", "Delete Error");
          return { error: "Delete Error" };
        }
      } catch (error) {
        showError("Error", "Delete Error");
        console.error("Error in deleteDivision:", error);
        return { error: "Delete Error" };
      }
    },
  }))
);

export default useDivisionStore;
